import { i18nNsFactory } from '@atlas-frontend/i18n';
import { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { TextLink } from '../../common/components/typography';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { usePanelParams } from '../usePanelParams';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import { SystemClassifierItemDto } from '../../systemClassifier';
import { DataSourceFormDataType } from '../schema';
import translations from '../translations/DataSourcesTable.translations.json';
import { DataSourceViewPanel } from './DataSourceViewPanel';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Table } from '../../common/tanstacktable';
import { useSystemClassifierItems } from '../../configuration';

const { addTranslations, useTranslation } = i18nNsFactory('data-sources-table');
addTranslations(translations);

export interface DataSourcesViewTableProps {
  dataSources?: DataSourceFormDataType[];
}

const columnHelper = createColumnHelper<DataSourceFormDataType>();

export function DataSourcesViewTable({
  dataSources,
}: DataSourcesViewTableProps) {
  const { t } = useTranslation();
  const { systemClassifierItems: sourceTypes } =
    useSystemClassifiers('DATA_SOURCE_TYPE').DATA_SOURCE_TYPE;
  const type = useMemo(
    () => dataSources?.[0]?.sourceType ?? 'FILE',
    [dataSources],
  );

  const [{ dataSourceId, dataSourceIndex }] = usePanelParams();
  const viewableDataSource = useMemo(
    () =>
      dataSourceId
        ? dataSources?.find(({ id }) => id === Number(dataSourceId))
        : dataSources?.[Number(dataSourceIndex)],
    [dataSources, dataSourceId, dataSourceIndex],
  );

  const methodNames = useSystemClassifierItems('QUERY_METHOD_NAME');

  const columns = useMemo(
    () => [
      columnHelper.accessor('sourceType', {
        header: t('column.sourceType'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 150 },
        cell: (props) => (
          <SourceTypeCell {...props} sourceTypes={sourceTypes} />
        ),
      }),
      ...(type === 'DATABASE'
        ? [
            columnHelper.accessor('code', {
              header: t('column.code'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 100 },
            }),
            columnHelper.accessor('name', {
              header: t('column.name'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: {
                width: '30%',
                minWidth: 150,
              },
            }),
          ]
        : []),
      columnHelper.accessor('dataLocation', {
        header: t(`sourceType.${type}`),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '30%',
          minWidth: 150,
        },
      }),
      ...(type === 'URL'
        ? [
            columnHelper.accessor('methodNameId', {
              header: t('column.methodNameId'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 100 },
              cell: (ctx) =>
                methodNames.find(
                  (methodName) => methodName.id === ctx.getValue(),
                )?.name,
            }),
          ]
        : []),
      columnHelper.accessor('connectionId', {
        header: t('column.connectionId'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '30%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('groupSource', {
        header: t('column.groupSource'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 100 },
        cell: memo((props: CellContext<DataSourceFormDataType, any>) =>
          t(props.renderValue() ? 'groupSource.yes' : 'groupSource.no'),
        ),
      }),
    ],
    [t, type, sourceTypes],
  );
  const [, setPanelParams] = usePanelParams();

  return (
    <>
      <TableWrapper>
        <Table
          name="dataSources"
          data={dataSources ?? []}
          columns={columns}
          rowKey="id"
          key={type}
        />
      </TableWrapper>
      {viewableDataSource && (
        <DataSourceViewPanel
          dataSource={viewableDataSource}
          onClose={() => setPanelParams({ dataSourceIndex: undefined })}
        />
      )}
    </>
  );
}

const SourceTypeCell: FC<
  CellContext<DataSourceFormDataType, any> & {
    sourceTypes: SystemClassifierItemDto[];
  }
> = memo((props) => {
  const [, setPanelParams] = usePanelParams();
  const dataSourceIndex = props.row.index;
  const dataSourceId = props.row.original.id;
  const params = dataSourceId ? { dataSourceId } : { dataSourceIndex };
  return (
    <TextLink
      id={`dataSources-${dataSourceIndex}-sourceType`}
      href={useHrefWithQueryParams(params)}
      onClick={suppressHref(() => setPanelParams(params))}
    >
      {props.sourceTypes.find((st) => st.code === props.renderValue())?.name}
    </TextLink>
  );
});

const TableWrapper = styled.div`
  width: 100%;
`;
